import React, { Suspense, lazy, useEffect, useState } from "react";
import { Switch, Route, useLocation } from "react-router-dom";
import { trackPageView } from "services/statsService";
import { NotFound } from "navigation/NotFound";
import { Spin } from "antd";
import ErrorBoundary from "components/ErrorBoundary";
import {
  ROUTE_LOGIN,
  ROUTE_DASHBOARD,
  ROUTE_USER_PROFILE,
  ROOT,
  ROUTE_REGISTER,
  ROUTE_HOME,
  ROUTE_WELCOME,
  ROUTE_SIGNUP,
  ROUTE_FORGOT_PASSWORD,
  ROUTE_CATEGORY,
  ROUTE_INFO,
  ROUTE_PAY,
  ROUTE_CONCESSION_PAY,
  ROUTE_FACILITY_CALENDAR,
} from "navigation/CONSTANTS";
import PrivateRoute from "./Auth/PrivateRoute";
import { useDispatch } from "react-redux";
import { ChangeSize } from "redux/actions/appActions";
import HomePage from "pages/HomePage/HomePage";

// Retries a dynamic import up to `retries` times with exponential backoff
const retryImport = (factory, retries = 3, delay = 800) =>
  factory().catch((err) => {
    if (retries === 0) throw err;
    return new Promise((resolve) => setTimeout(resolve, delay)).then(() =>
      retryImport(factory, retries - 1, delay * 2)
    );
  });

// Wraps React.lazy with retry + normalises undefined/null rejections to real Errors
const safeLazy = (factory) =>
  lazy(() =>
    retryImport(factory).catch((err) => {
      throw err instanceof Error
        ? err
        : new Error(err != null ? String(err) : "ChunkLoadError: chunk failed to load");
    })
  );

const LoginPage = safeLazy(() => import("pages/LoginPage/LoginPage"));
const SignupPage = safeLazy(() => import("pages/SignupPage/SignupPage"));
const ForgotPasswordPage = safeLazy(() => import("pages/ForgotPasswordPage/ForgotPasswordPage"));
const RegisterPage = safeLazy(() => import("pages/RegisterPage/RegisterPage"));
const CategoryPage = safeLazy(() => import("pages/CategoryPage/CategoryPage"));
const InfoPage = safeLazy(() => import("pages/InfoPage/InfoPage"));
const DashboardPage = safeLazy(() => import("pages/DashboardPage/Dashboard"));
const WelcomePage = safeLazy(() => import("pages/WelcomePage/WelcomePage"));
const FacilityPaymentPage = safeLazy(() => import("pages/FacilityPaymentPage/FacilityPaymentPage"));
const ConcessionPaymentPage = safeLazy(() => import("pages/ConcessionPaymentPage/ConcessionPaymentPage"));
const FacilityCalendarPage = safeLazy(() => import("pages/FacilityCalendarPage/FacilityCalendarPage"));
// Fires a page_view event on every route change
const RouteTracker = () => {
  const location = useLocation();
  useEffect(() => {
    trackPageView();
  }, [location.pathname]);
  return null;
};

export const RouterConfig = () => {
  const screenSize = useWindowSize();
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(
      ChangeSize({ width: screenSize.width, height: screenSize.height })
    );
  }, [screenSize]);

  return (
    <div>
      <RouteTracker />
      <ErrorBoundary>
        <Suspense fallback={
          <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Spin size="large" />
          </div>
        }>
          <Switch>
          <Route exact path={[ROOT, "/index.html"]} component={HomePage} />
          <Route exact path={ROUTE_CATEGORY} component={CategoryPage} />
          <Route exact path={ROUTE_INFO} component={InfoPage} />
          <Route exact path={ROUTE_LOGIN} component={LoginPage} />
          <Route exact path={ROUTE_SIGNUP} component={SignupPage} />
          <Route exact path={ROUTE_FORGOT_PASSWORD} component={ForgotPasswordPage} />
          <Route exact path={ROUTE_REGISTER} component={RegisterPage} />
          <Route exact path={ROUTE_WELCOME} component={WelcomePage} />
          <Route exact path={ROUTE_PAY} component={FacilityPaymentPage} />
          <Route exact path={ROUTE_CONCESSION_PAY} component={ConcessionPaymentPage} />
          <Route exact path={ROUTE_FACILITY_CALENDAR} component={FacilityCalendarPage} />
          <PrivateRoute exact path={ROUTE_DASHBOARD} component={DashboardPage} />
          <Route path="*" component={NotFound} />
        </Switch>
        </Suspense>
      </ErrorBoundary>
    </div>
  );
};

// Hook
function useWindowSize() {
  // Initialize state with undefined width/height so server and client renders match
  // Learn more here: https://joshwcomeau.com/react/the-perils-of-rehydration/
  const [windowSize, setWindowSize] = useState({
    width: undefined,
    height: undefined,
  });

  useEffect(() => {
    // Handler to call on window resize
    function handleResize() {
      // Set window width/height to state
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    }

    // Add event listener
    window.addEventListener("resize", handleResize);

    // Call handler right away so state gets updated with initial window size
    handleResize();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleResize);
  }, []); // Empty array ensures that effect is only run on mount

  return windowSize;
}
